import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;
const APP_ENV = import.meta.env.VITE_APP_ENV || 'development';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // Important for handling cookies/sessions
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to auth for 401s if:
    // 1. We're not already on an auth endpoint
    // 2. We have a token (meaning we thought we were authenticated)
    if (error.response?.status === 401 && 
        !error.config.url.includes('/auth/') && 
        localStorage.getItem('token')) {
      localStorage.removeItem('token');
      window.location.href = '/auth';
    }
    // Log errors only in development
    if (APP_ENV === 'development') {
      console.error('API Error:', error);
    }
    return Promise.reject(error);
  }
);

export const auth = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

export const recipes = {
  list: (params) => api.get('/recipes', { params }),
  get: (id) => api.get(`/recipes/${id}`),
  create: (data) => api.post('/recipes', data),
  update: (id, data) => api.put(`/recipes/${id}`, data),
  delete: (id) => api.delete(`/recipes/${id}`),
};

export const oils = {
  list: (params) => api.get('/oils', { params }),
  get: (id) => api.get(`/oils/${id}`),
};

export const calculator = {
  calculate: (data) => api.post('/calculator', data),
};

export const forums = {
  list: (params) => api.get('/forums', { params }),
  get: (id) => api.get(`/forums/${id}`),
  create: (data) => api.post('/forums', data),
  update: (id, data) => api.put(`/forums/${id}`, data),
  delete: (id) => api.delete(`/forums/${id}`),
};

export const posts = {
  list: (params) => api.get('/posts', { params }),
  get: (id) => api.get(`/posts/${id}`),
  create: (data) => api.post('/posts', data),
  update: (id, data) => api.put(`/posts/${id}`, data),
  delete: (id) => api.delete(`/posts/${id}`),
};

export const profiles = {
  list: (params) => api.get('/profiles', { params }),
  get: (id) => api.get(`/profiles/${id}`),
  update: (id, data) => api.put(`/profiles/${id}`, data),
};

// Admin API endpoints
export const users = {
  list: () => api.get('/admin/users'),
  get: (id) => api.get(`/admin/users/${id}`),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
};

export const adminOils = {
  export: () => api.get('/admin/oils/export', { responseType: 'blob' }),
  importCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/oils/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  get: (id) => api.get(`/admin/oils/${id}`),
  create: (data) => api.post('/admin/oils', data),
  update: (id, data) => api.put(`/admin/oils/${id}`, data),
  delete: (id) => api.delete(`/admin/oils/${id}`),
};

export const roles = {
  list: () => api.get('/admin/roles'),
};

export const additives = {
  list: (params) => api.get('/additives', { params }),
  search: (query) => api.get('/additives/search', { params: { query } }),
  get: (id) => api.get(`/additives/${id}`),
  create: (data) => api.post('/additives', data),
  update: (id, data) => api.put(`/additives/${id}`, data),
  delete: (id) => api.delete(`/additives/${id}`),
};

export default api; 